import React, { useState } from 'react';
import {
    CssBaseline,
    Container,
    Box,
    AppBar,
    Toolbar,
    Typography,
    Tabs,
    Tab,
    ThemeProvider,
    createTheme,
    Paper
} from '@mui/material';
import { PartnershipProvider } from './contexts/PartnershipContext';
import PartnershipForm from './components/PartnershipForm';
import ResultsDisplay from './components/ResultsDisplay';
import ComparisonView from './components/ComparisonView';
import { PartnershipScenario } from './types/types';
import { usePartnership } from './contexts/PartnershipContext';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
};

const theme = createTheme({
    palette: {
        primary: {
            main: '#33bbdf', // Blue
            light: '#5dc7e8',
            dark: '#2a9bb8',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#4ECDC4', // Teal accent
            light: '#7ED7D0',
            dark: '#3EA39C',
            contrastText: '#ffffff',
        },
        background: {
            default: '#F7F9FC',
            paper: '#FFFFFF',
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1)',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 4px 8px rgba(51, 187, 223, 0.2)',
                        },
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #33bbdf 0%, #5dc7e8 100%)',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px 8px 0 0',
                        margin: '0 4px',
                        '&.Mui-selected': {
                            color: '#33bbdf',
                        },
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#33bbdf',
                        height: '3px',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 700,
        },
        h2: {
            fontWeight: 600,
        },
        h3: {
            fontWeight: 600,
        },
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
});

const AppContent: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedScenario, setSelectedScenario] = useState<PartnershipScenario | null>(null);
    const { getCalculatedResults, addScenario } = usePartnership();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleFormSubmit = (data: Omit<PartnershipScenario, 'id'>) => {
        const created = addScenario(data);
        setSelectedScenario(created);
        setActiveTab(1);
    };

    return (
        <>
            <AppBar position="static" elevation={1}>
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        Partnership ROI Calculator
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                aria-label="calculator tabs"
                                sx={{
                                    '& .MuiTab-root': {
                                        minHeight: 48,
                                    },
                                }}
                            >
                                <Tab label="Calculator" />
                                <Tab label="Results" disabled={!selectedScenario} />
                                <Tab label="Compare" />
                            </Tabs>
                        </Box>

                        <TabPanel value={activeTab} index={0}>
                            <Box sx={{ p: 3 }}>
                                <PartnershipForm onSubmit={handleFormSubmit} />
                            </Box>
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            {selectedScenario && (
                                <Box sx={{ p: 3 }}>
                                    <ResultsDisplay
                                        scenario={selectedScenario}
                                        results={getCalculatedResults(selectedScenario.id)!}
                                    />
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Box sx={{ p: 3 }}>
                                <ComparisonView />
                            </Box>
                        </TabPanel>
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <PartnershipProvider>
                <AppContent />
            </PartnershipProvider>
        </ThemeProvider>
    );
};

export default App;
